import { BooleanInput } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { Constants } from 'app/core/constant/app-constant';
import { EventType, PasswordType } from 'app/core/enums';
import { RoleList } from 'app/core/enums/role-list.enum';
import { User } from 'app/core/models/filmmaker-user.model';
import { LoginDataModel } from 'app/core/models/login.model';
import { TranslatePipe } from 'app/core/pipe/translate.pipe';
import { Analytics } from 'app/core/services/analytics/analytics.service';
import { PasswordService } from 'app/core/services/password.service';
import { UserCountryService } from 'app/core/services/user-country.service';
import { TenantConfigService } from 'app/core/tenant';
import {
    ChangePasswordDialogComponent,
    ChangePasswordDialogData,
} from 'app/shared/components/molecules/change-password-dialog/change-password-dialog.component';
import { ProfileCompletionGateService } from 'app/shared/components/molecules/profile-completion-gate/profile-completion-gate.service';
import { ReferAndEarnDialogService } from 'app/shared/services/refer-and-earn-dialog.service';
import { UserService } from 'app/shared/services/user.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatMenuModule,
        MatIconModule,
        MatDividerModule,
        TranslatePipe,
        RouterModule,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    static ngAcceptInputType_showAvatar: BooleanInput;

    @Input() showAvatar = true;
    user: User | LoginDataModel | null = null;
    currentRole: string = RoleList.FilmMaker;
    roles = RoleList;

    private readonly _tenantConfig = inject(TenantConfigService);

    /** Tenant feature flags — computed from the already-initialized service. */
    get features() {
        return this._tenantConfig.getFeatures();
    }

    /** Returns the icon name to overlay on the profile avatar based on profileStatus */
    get profileStatusIcon(): string | null {
        if (this.currentRole !== RoleList.FilmMaker) return null;
        const status = (this.user as User)?.profileStatus;
        if (!status) return null;
        switch (status) {
            case 'Incomplete':
            case 'Created':
            case 'Submitted':
            case 'In Review':
                return 'pending';
            case 'Error':
                return 'error';
            case 'Approved':
                return 'check_circle';
            default:
                return null;
        }
    }

    /** Returns the CSS color class for the profile status icon */
    get profileStatusIconClass(): string {
        const status = (this.user as User)?.profileStatus;
        if (status === 'Error') return 'text-red-500';
        if (status === 'Approved') return 'text-green-500';
        return 'text-amber-500';
    }

    private _unsubscribeAll: Subject<void> = new Subject<void>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _authService: AuthService,
        private readonly _referAndEarnDialogService: ReferAndEarnDialogService,
        private readonly _analytics: Analytics,
        private readonly _dialog: MatDialog,
        private readonly _passwordService: PasswordService,
        private readonly _userCountryService: UserCountryService,
        private readonly _gate: ProfileCompletionGateService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        // this._userService.user$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((user: User) => {
        //         this.user = user;

        //         // Mark for check
        //         this._changeDetectorRef.markForCheck();
        //     });

        this._authService.currentUserSubject
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user) => {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        this._authService.currentUserRoleSubject
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((role) => {
                this.currentRole = role;
            });

        // Refresh profile status for filmmakers to keep badge up-to-date
        this._refreshProfileStatus();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Refresh the filmmaker profile status from the backend
     * to keep the avatar badge up-to-date without requiring logout.
     */
    private _refreshProfileStatus(): void {
        if (this.currentRole !== RoleList.FilmMaker) return;

        this._userService
            .refreshProfile()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (profile) => {
                    const currentUser =
                        this._authService.currentUserSubject.value;
                    if (currentUser && profile.profileStatus) {
                        const updatedUser = {
                            ...currentUser,
                            profileStatus: profile.profileStatus,
                            profileRejectionMessage:
                                profile.profileRejectionMessage,
                            profileRejectionFields:
                                profile.profileRejectionFields,
                        };
                        this._authService.currentUserSubject.next(updatedUser);
                        this._authService._saveUser(updatedUser as User);
                    }
                },
                error: () => {
                    // Silently fail — badge stays as cached
                },
            });
    }

    /**
     * Navigate to refer and earn
     */
    navigateToReferAndEarn(): void {
        if (!this._gate.isApproved()) {
            this._gate.showGate('Refer & Earn');
            return;
        }
        // EventType.click → CleverTap: "Button Click" with button: "Menu Item Clicked"
        this._analytics.customAnalytics('Menu Item Clicked', {
            eventType: EventType.click,
            menuItem: 'Refer And Earn',
            source: 'user_menu',
        });

        // Generate a referral code based on user ID or use a default
        this._referAndEarnDialogService.getUserReferralCode().subscribe({
            next: (response) => {
                if (response.status === 200) {
                    // EventType.api → CleverTap: "API Success" with all params
                    this._analytics.customAnalytics('Get Referral Code', {
                        eventType: EventType.apiSuccess,
                        apiName: 'Get Referral Code',
                        referralCode: response.data.referralCode,
                        hasDescription: !!response.data.content,
                    });

                    this._referAndEarnDialogService.openReferAndEarnDialog({
                        referralCode: response.data.referralCode,
                        title: response.data.title,
                        description: response.data.description,
                        contentForFilmMaker: response.data.contentForFilmMaker,
                        contentForViewer: response.data.contentForViewer,
                    });
                }
            },
            error: (error) => {
                // EventType.apiError → CleverTap: "API Failure" with name: "Get Referral Code Failed"
                this._analytics.failureEvent('Get Referral Code Failed', {
                    error: error,
                    reason:
                        error?.error?.message ||
                        'Failed to fetch referral code',
                });
            },
        });
    }

    /**
     * Sign out
     *
     * Do NOT call logout() here — the /sign-out component owns the logout call.
     * Calling logout() before navigating would clear the token, causing AuthGuard
     * to block the /sign-out route and skip the sign-out screen entirely.
     */
    navigateToProfile(): void {
        if (!this._gate.isApproved()) {
            this._gate.showGate('My Profile').subscribe();
            return;
        }
        this._router.navigate(['/user/profile']);
    }

    signOut(): void {
        this._router.navigate(['/sign-out']);
    }

    /**
     * Open change password dialog
     */
    changePassword(): void {
        if (!this._gate.isApproved()) {
            this._gate.showGate('Password');
            return;
        }
        if (!this.user) {
            return;
        }

        // Get user identifier (email or phone)
        // Note: currentUserSubject returns User type which has phoneNumber and email
        const currentUser = this._authService.currentUserSubject.value as User;

        // Prepare dialog data
        const dialogData: ChangePasswordDialogData = {
            email: currentUser?.email || this.user?.email,
            phoneNumber: currentUser?.phoneNumber,
            countryCode:
                currentUser?.countryCode || Constants.IndianDetails.countryCode,
            countryCodeNumeric:
                currentUser?.countryCodeNumeric ||
                Constants.IndianDetails.dialCode,
            isPasswordSet: currentUser?.isPasswordSet,
        };

        // EventType.click → Analytics: "Button Click" with button: "Change Password"
        this._analytics.customAnalytics('Menu Item Clicked', {
            eventType: EventType.click,
            menuItem: 'Change Password',
            source: 'user_menu',
        });

        // Open dialog
        const dialogRef = this._dialog.open(ChangePasswordDialogComponent, {
            width: '600px',
            maxWidth: '90vw',
            data: dialogData,
            disableClose: true,
        });

        // Handle dialog result
        dialogRef.afterClosed().subscribe((result) => {
            if (result?.verified && result?.identifier) {
                // Navigate to update password page with verified identifier
                this._router.navigate(['user', 'update-password'], {
                    queryParams: {
                        identifier: result.identifier,
                        type: PasswordType.profile,
                    },
                });
            }
        });
    }
}
